/**
 * sendClientAccess — staff-triggered "Send access to client".
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner-confirmed 2026-06-15 (CORRECTED). On the Client Detail screen a staff
 *  member sends the client a sign-in LINK over BOTH email (Brevo) and WhatsApp
 *  (Meta Cloud API). The destination is the unified /login (Google-primary). This is
 *  a LINK send — it must NOT fire a 4-digit OTP code. The code remains ONLY as
 *  a fallback the client chooses AFTER clicking the link, on /login, if they
 *  have no Google account ("Use a code instead").
 *
 *  Vehicle: the `sendClientPortalLink` callable (avbp-functions
 *  `avbp_client_link.js`). It resolves the client by handle (email · phone ·
 *  client# · project#) and emails + WhatsApps the /login URL + the "sign in
 *  with the same email you gave Aayatan Veda" note. NO OTP minted or sent.
 *  (The earlier build wrongly reused `requestClientOtp`, which sends a code —
 *  that was the bug this corrects.)
 *
 *  This module is the pure picker + result-interpreter (browser-free,
 *  unit-tested); the .tsx owns the httpsCallable round trip + the toast.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Per-channel outcome as reported by the callable (added 2026-07-21). */
export interface ChannelResult {
  /** Did we even have an address/number for this channel? */
  attempted: boolean;
  /** Did it actually go out? */
  ok: boolean;
  /** Masked destination, ONLY when ok. */
  to: string | null;
  /** Machine reason when it did not go out. */
  error: string | null;
  /**
   * true = the send structurally could not happen (e.g. the WhatsApp template
   * is not on the approved allow-list). Retrying will not help — a human has
   * to fix the configuration.
   */
  undeliverable?: boolean;
}

/** Response shape of the `sendClientPortalLink` callable (no OTP fields). */
export interface SendLinkResp {
  ok: boolean;
  /** true when at least one channel sent AND at least one did not. */
  partial?: boolean;
  channels?: { email: ChannelResult; whatsapp: ChannelResult };
  /** Legacy shape — successes only. Kept for older backends. */
  sentTo?: { email: string | null; phone: string | null };
  found?: boolean;
  locked?: boolean;
  error?: 'no_destinations' | 'delivery_failed' | string;
}

/** The minimal client shape the picker needs (a subset of LiveClient). */
export interface ClientAccessTarget {
  clientNo: string;
  email?: string | null;
  phone?: string | null;
}

/**
 * Pick the handle to send to. Prefer the EMAIL, then phone, then the client
 * NUMBER. Rationale: the resolver matches email / phone by exact-equality
 * query (its strongest paths); the client number is the last-resort key.
 * Returns null only when there is genuinely nothing to key on.
 */
export function pickAccessHandle(client: ClientAccessTarget): string | null {
  const email = (client.email || '').trim();
  if (email) return email;
  const phone = (client.phone || '').trim();
  if (phone) return phone;
  const clientNo = (client.clientNo || '').trim();
  if (clientNo) return clientNo;
  return null;
}

/**
 * `true` when the client has at least one delivery channel (email or phone).
 * The backend reports `no_destinations` otherwise; surfacing it up front lets
 * the UI explain the gap instead of firing a doomed send.
 */
export function hasDeliveryChannel(client: ClientAccessTarget): boolean {
  return Boolean((client.email || '').trim() || (client.phone || '').trim());
}

/** A human summary of where the access prompt landed (success path). */
export function describeSentTo(
  sentTo: { email: string | null; phone: string | null } | undefined,
): string {
  const email = sentTo?.email || null;
  const phone = sentTo?.phone || null;
  if (email && phone) return `${email} and ${phone}`;
  if (email) return email;
  if (phone) return phone;
  return 'the client';
}

/** Outcome of a send-access attempt, ready to feed a toast. */
export interface SendAccessOutcome {
  ok: boolean;
  message: string;
  /** true when something landed but something else did not. */
  partial?: boolean;
}

const CHANNEL_LABEL: Record<'email' | 'whatsapp', string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
};

/**
 * describeChannels — PURE. A plain-English, per-channel account of what went
 * out and what did not.
 *
 * WHY (2026-07-21 audit §2 #1): the old toast said "Sign-in link sent to
 * <email>" whenever the callable returned ok — even when the WhatsApp half had
 * silently failed. Staff had no way to know. A failed channel must be named.
 */
export function describeChannels(
  channels: { email: ChannelResult; whatsapp: ChannelResult },
): { sent: string[]; failed: string[] } {
  const sent: string[] = [];
  const failed: string[] = [];
  (['email', 'whatsapp'] as const).forEach((key) => {
    const c = channels[key];
    if (!c || !c.attempted) return;
    if (c.ok) {
      sent.push(`${CHANNEL_LABEL[key]}${c.to ? ` (${c.to})` : ''}`);
    } else {
      failed.push(
        `${CHANNEL_LABEL[key]}${
          c.undeliverable ? ' (not configured to send — tell the tech team)' : ' (failed)'
        }`,
      );
    }
  });
  return { sent, failed };
}

const SIGN_IN_TAIL =
  'They tap it to open portal.aayatanveda.in and sign in with Google (or a code if they have no Gmail). They must use the same email you have on file.';

/** Interpret the sendClientPortalLink response into a toast-ready outcome. */
export function interpretSendAccess(resp: SendLinkResp): SendAccessOutcome {
  if (resp.ok) {
    // Preferred path: the backend told us channel by channel. Never summarise
    // away a failure — name every channel that did NOT go out.
    if (resp.channels) {
      const { sent, failed } = describeChannels(resp.channels);
      if (failed.length === 0) {
        return {
          ok: true,
          partial: false,
          message: `Sign-in link sent by ${sent.join(' and ')}. ${SIGN_IN_TAIL}`,
        };
      }
      const sentPart = sent.length
        ? `Sign-in link sent by ${sent.join(' and ')} ONLY.`
        : 'Sign-in link was NOT sent.';
      return {
        // ok:false so the toast is styled as a problem — a half-send IS a problem.
        ok: false,
        partial: sent.length > 0,
        message:
          `${sentPart} NOT sent by ${failed.join(' and ')}. ` +
          `Check the client can be reached the other way before you rely on this. ${SIGN_IN_TAIL}`,
      };
    }
    // Legacy backend (no `channels`) — fall back to the old summary.
    return {
      ok: true,
      partial: false,
      message: `Sign-in link sent to ${describeSentTo(resp.sentTo)} — they tap it to open portal.aayatanveda.in and sign in with Google (or a code if they have no Gmail). They must use the same email you have on file.`,
    };
  }
  if ('found' in resp && resp.found === false) {
    return {
      ok: false,
      message: 'Could not match this client for sign-in. Check their email / phone / client number on file.',
    };
  }
  if ('locked' in resp && resp.locked) {
    return {
      ok: false,
      message: 'This client is temporarily locked out after repeated attempts. Try again later.',
    };
  }
  if ('error' in resp && resp.error === 'no_destinations') {
    return {
      ok: false,
      message: 'No email or phone on file for this client — add a contact detail first, then resend.',
    };
  }
  if ('error' in resp && resp.error === 'delivery_failed') {
    return {
      ok: false,
      message: 'We could not deliver the sign-in link (email + WhatsApp both failed). Try again shortly.',
    };
  }
  return { ok: false, message: 'Could not send sign-in access. Please try again.' };
}
